import React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { AxiosError } from 'axios';
import throttle from 'lodash/throttle';
import { useIntl } from 'react-intl';

import { LIST_EVENTS_RESOURCE, useListEvents } from 'src/features/eventLog/api/useListEvents';
import CellEventCustomer from 'src/features/eventLog/components/cell/CellEventCustomer';
import CellEventLogMessage from 'src/features/eventLog/components/cell/CellEventLogMessage';
import { EVENT_TYPE, EventLog } from 'src/features/eventLog/types';
import { eventTypeBadgeOptions } from 'src/features/eventLog/utils/constants';
import { eventMessages } from 'src/features/eventLog/utils/messages';
import { SEARCH_GATEWAY_RESOURCE, searchGateway } from 'src/features/gateway/api/useSearchGateway';
import GatewaySidePanel, { GATEWAY_SIDE_PANEL_NAME } from 'src/features/gateway/components/GatewaySidePanel';
import { searchStay, SEARCH_STAY_RESOURCE } from 'src/features/stay/api/useSearchStay';
import { ACTIVE_STAYS_PANEL_NAME } from 'src/features/stay/components/ActiveStaysTable';
import { PAST_STAYS_PANEL_NAME } from 'src/features/stay/components/PastStaysTable';
import ActiveStaysSidePanel from 'src/features/stay/components/sidePanel/ActiveStaysSidePanel';
import PastStaysSidePanel from 'src/features/stay/components/sidePanel/PastStaysSidePanel';
import { searchVisitor, SEARCH_VISITOR_RESOURCE } from 'src/features/visitor/api/useSearchVisitor';
import VisitorSidePanel, { VISITOR_SIDE_PANEL_NAME } from 'src/features/visitor/components/VisitorSidePanel';
import WSContext, { WebSocketGroup } from 'src/features/visitor/context/WSContext';
import { WebSocketEvent } from 'src/features/visitor/types';
import { NEW_STAY_AND_PASS_EVENTS } from 'src/features/visitor/utils/wsEvents';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import SidePanel from 'src/registry/components/sidePanel/SidePanel';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { useSidePanel } from 'src/registry/providers/SidePanelProvider';
import { generalErrorMessages } from 'src/registry/utils/messages';
import { commonMessages } from 'src/utils/messages';

export default function EventLogTable() {
    const { formatMessage } = useIntl();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting({ initialSortingState: [{ id: 'timestamp', desc: true }] });
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryFiltersOptions, ...filters } = useFilters();

    const { subscribe, unsubscribe } = React.useContext(WSContext);

    React.useEffect(() => {
        const handleWebSocket = throttle((event: WebSocketEvent) => {
            if (NEW_STAY_AND_PASS_EVENTS.includes(event.message)) {
                void queryClient.invalidateQueries({
                    queryKey: [LIST_EVENTS_RESOURCE],
                });
            }
        }, 500);
        subscribe(WebSocketGroup.GATEWAY_PASS, handleWebSocket);

        return () => {
            handleWebSocket.cancel();
            unsubscribe(WebSocketGroup.GATEWAY_PASS, handleWebSocket);
        };
    }, [subscribe, unsubscribe]);

    const { parkingId } = useParking();

    const {
        data: eventLogData,
        isError,
        isLoading,
    } = useListEvents({
        parkingId,
        ...queryPaginationOptions,
        ...querySortingOptions,
        ...querySearchOptions,
        ...queryFiltersOptions,
    });

    const [defaultDateFilter] = React.useState(() => {
        const now = Date.now();

        return {
            afterDate: new Date(now - 30 * 24 * 60 * 60 * 1000),
            beforeDate: new Date(),
        };
    });

    const { openPanel, closePanel } = useSidePanel();
    const eventLogSidePanels = [
        ACTIVE_STAYS_PANEL_NAME,
        PAST_STAYS_PANEL_NAME,
        VISITOR_SIDE_PANEL_NAME,
        GATEWAY_SIDE_PANEL_NAME,
    ];

    const closeRemainingPanels = (remainOpenPanel: string) => {
        eventLogSidePanels.forEach((panel) => {
            if (panel !== remainOpenPanel) {
                closePanel(panel);
            }
        });
    };

    const [selectedStay, setSelectedStay] = React.useState<{ id: string; isActive: boolean } | null>(null);
    const [selectedVisitor, setSelectedVisitor] = React.useState<string | undefined>(undefined);
    const [selectedGateway, setSelectedGateway] = React.useState<string | undefined>(undefined);
    const [selectedEventType, setSelectedEventType] = React.useState<EVENT_TYPE | undefined>(undefined);
    const [eventPhotoId, setEventPhotoId] = React.useState<string | undefined>(undefined);

    // Resolve the visitor by the identifier value shown in the customer column and open its dashboard.
    // Used for events that only carry an identifier (e.g. a license plate) without a visitor id
    const handleIdentifierClick = async (identifier: string, eventType?: EVENT_TYPE, photoId?: string) => {
        try {
            const visitors = await queryClient.fetchQuery({
                queryKey: [SEARCH_VISITOR_RESOURCE, parkingId, identifier],
                queryFn: () => searchVisitor({ parkingId, search: identifier }),
            });
            const visitorId = visitors.results?.[0]?.id;
            if (!visitorId) {
                toast({
                    title: formatMessage(generalErrorMessages.somethingWentWrong),
                    description: formatMessage(eventMessages.visitorNotFound),
                    variant: 'destructive',
                });
                return;
            }
            setEventPhotoId(photoId);
            setSelectedVisitor(visitorId);
            openPanel(VISITOR_SIDE_PANEL_NAME);
            setSelectedEventType(eventType);
            closeRemainingPanels(VISITOR_SIDE_PANEL_NAME);
        } catch (error) {
            // Show the backend's message for client errors (4xx); for server errors (5xx) or no
            // response keep the generic message
            const response = (error as AxiosError<string>).response;
            const description =
                response && response.status < 500 && typeof response.data === 'string' && response.data
                    ? response.data
                    : formatMessage(generalErrorMessages.unknownError);
            toast({
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description,
                variant: 'destructive',
            });
        }
    };

    const handleGatewayClick = async (identifier: string) => {
        try {
            const gateways = await queryClient.fetchQuery({
                queryKey: [SEARCH_GATEWAY_RESOURCE, identifier],
                queryFn: () => searchGateway({ search: identifier, parkingId }),
            });
            const gatewayId = gateways.results?.[0]?.id;
            if (!gatewayId) {
                toast({
                    title: formatMessage(generalErrorMessages.somethingWentWrong),
                    description: formatMessage(eventMessages.visitorNotFound),
                    variant: 'destructive',
                });
                return;
            }
            setSelectedGateway(gatewayId);
            openPanel(GATEWAY_SIDE_PANEL_NAME);
            closeRemainingPanels(GATEWAY_SIDE_PANEL_NAME);
        } catch (error) {
            // Show the backend's message for client errors (4xx); for server errors (5xx) or no
            // response keep the generic message
            const response = (error as AxiosError<string>).response;
            const description =
                response && response.status < 500 && typeof response.data === 'string' && response.data
                    ? response.data
                    : formatMessage(generalErrorMessages.unknownError);
            toast({
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description,
                variant: 'destructive',
            });
        }
    };

    // Search the stay by ref (active first, then past) and open the matching side panel
    const handleRefClick = async (ref: string) => {
        try {
            const stay = await queryClient.fetchQuery({
                queryKey: [SEARCH_STAY_RESOURCE, parkingId, ref],
                queryFn: () => searchStay({ parkingId, ref }),
            });
            const isActive = stay.exit_time === null;
            const activePastStayPanel = isActive ? ACTIVE_STAYS_PANEL_NAME : PAST_STAYS_PANEL_NAME;
            setSelectedStay({ id: stay.id, isActive });
            openPanel(activePastStayPanel);
            closeRemainingPanels(activePastStayPanel);
        } catch (error) {
            // Show the backend's message for client errors (4xx, e.g. "stay not found"); for server
            // errors (5xx) or no response keep the generic message
            const response = (error as AxiosError<string>).response;
            const description =
                response && response.status < 500 && typeof response.data === 'string' && response.data
                    ? response.data
                    : formatMessage(generalErrorMessages.unknownError);
            toast({
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description,
                variant: 'destructive',
            });
        }
    };

    const columnHelper = createColumnHelper<EventLog>();

    const columns = [
        columnHelper.accessor('timestamp', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(commonMessages.time),
                inputType: INPUT_TYPE.DATE,
                dateFilter: defaultDateFilter,
            },
            maxSize: 120,
        }),
        columnHelper.accessor('event_type', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(eventMessages.type),
                badgeOptions: eventTypeBadgeOptions,
            },
        }),
        columnHelper.accessor('event_data', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => (
                <CellEventCustomer
                    eventData={row.original.event_data}
                    event={row.original}
                    onIdentifierClick={handleIdentifierClick}
                />
            ),
            meta: {
                title: formatMessage(commonMessages.customer),
            },
        }),
        columnHelper.accessor('id', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => (
                <CellEventLogMessage
                    eventData={row.original.event_data}
                    event={row.original}
                    onRefClick={handleRefClick}
                    handleIdentifierClick={handleIdentifierClick}
                    handleGatewayClick={handleGatewayClick}
                />
            ),
            meta: {
                title: formatMessage(eventMessages.message),
            },
        }),
    ];

    return (
        <>
            <DataTable<EventLog, any>
                isLoading={isLoading}
                isError={isError}
                columns={columns}
                data={eventLogData}
                pagination={pagination}
                sorting={sorting}
                search={search}
                filters={filters}
                semantics={{
                    title: formatMessage(eventMessages.eventLog),
                    entryName: formatMessage(eventMessages.eventLog).toLowerCase(),
                    entryNames: formatMessage(eventMessages.eventLogs).toLowerCase(),
                }}
                getRowId={(row) => row.id.toString()}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
                enableLocalFilters={false}
            />
            <SidePanel onClose={() => setSelectedStay(null)} name={ACTIVE_STAYS_PANEL_NAME}>
                <ActiveStaysSidePanel selectedStayId={selectedStay?.isActive ? selectedStay.id : undefined} />
            </SidePanel>
            <SidePanel onClose={() => setSelectedStay(null)} name={PAST_STAYS_PANEL_NAME}>
                <PastStaysSidePanel
                    selectedStayId={selectedStay && !selectedStay.isActive ? selectedStay.id : undefined}
                />
            </SidePanel>
            <SidePanel onClose={() => setSelectedVisitor(undefined)} name={VISITOR_SIDE_PANEL_NAME}>
                <VisitorSidePanel visitorId={selectedVisitor} eventType={selectedEventType} photoId={eventPhotoId} />
            </SidePanel>
            <SidePanel onClose={() => setSelectedGateway(undefined)} name={GATEWAY_SIDE_PANEL_NAME}>
                <GatewaySidePanel gatewayId={selectedGateway} />
            </SidePanel>
        </>
    );
}
